import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  CellContext,
  ColumnDef,
  ColumnDefTemplate,
  ColumnFiltersState,
  ColumnHelper,
  createColumnHelper,
  getCoreRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';

import { BmweDataColumn } from '../../types';
import { DistinctValuesList, fetchDistinctValues } from './distinctValues';
import { ColumnPickerMenu } from './parts/filterPin';
import { InfiniteQueryHook, PageInfo } from '../../types/graphql.type';
import { getNestedProperty } from '../../helper';
import BmweTableHeader from './bmweTableHeader';
import styles from './bmweDataTable.module.css';
import { useSortAnnouncement } from './hooks/useSortAnnouncement';
import { DataTableLiveRegions } from './parts/dataTableLiveRegions';
import { VirtualizedTableBody } from './parts/virtualizedTableBody';
import { useDebounce } from '../../hooks/useDebounce';
import { TableViewProvider, useTableView } from './context/tableView';
import { TableViewControl } from './parts/tableViewControl';
import { tableStrings } from './i18n/de-de';
import { FilterMode, TableView } from './types/tableView';
import { useTableLayout } from './hooks/useTableLayout';
import { definedSizeToScrollPx, parseTrack, toFitTracks } from './gridTracks';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { ErrorFallback } from '../errorFallback/errorFallback';
import { useHeaderSlot } from '../../context';

const SEARCH_SKELETON_ROWS = 5;
const EMPTY_ROWS: never[] = [];
const ACTION_TRACK = '2.5rem';

export interface TableQueryState {
  pageSize: number;
  cursor?: string;
  sorting: SortingState;
  filters: ColumnFiltersState;
  filterMode: FilterMode;
  globalSearch?: string;
}

export interface BmweDataTableProps<
  TRow extends Record<string, unknown>,
  TQuery,
  TVariables,
> {
  columns: BmweDataColumn<TRow>[];
  infiniteQueryHook: InfiniteQueryHook<TQuery, TVariables>;
  toTableQueryVariables: (state: TableQueryState) => TVariables;
  mapToRows: (pages: TQuery[]) => TRow[];
  getPageInfo: (lastPage?: TQuery) => PageInfo | undefined;
  getTotalCount: (lastPage?: TQuery) => number;
  tableKey: string;
  defaultViews?: TableView[];
  /** Lets text column filters offer the column's existing values; the backend decides per column. */
  distinctValuesList?: DistinctValuesList;

  pageSize?: number;
  initialSort?: SortingState;
  initialFilters?: ColumnFiltersState;
  enableMultiSort?: boolean;
  enableGlobalSearch?: boolean;
  enableLayoutControls?: boolean;
  globalSearchPlaceholder?: string;
  globalSearchSlot?: ReactNode;
  /** Runs the global search only on Enter or the search button instead of while typing. */
  searchOnSubmit?: boolean;
  /** Renders the global search bar in the page header's action row, if there is one. */
  globalSearchInHeader?: boolean;
  /** Shows a button inside the global search field that clears it. */
  clearableGlobalSearch?: boolean;
  debounceMs?: number;

  /**
   * Optional singular/plural labels used in the total-count display.
   * Defaults to the generic "Eintrag/Einträge". Pass entity-specific labels
   * (e.g. "Vorgang/Vorgänge") to make the count read naturally in context.
   */
  entityLabel?: { singular: string; plural: string };

  getRowId?: (row: TRow, index: number) => string;
  /** Rows are selected by click or Space; pass `getRowId` for ids that survive a refetch. */
  selectionMode?: 'none' | 'single' | 'multi';
  onSelectionChange?: (rows: TRow[]) => void;

  loadingSkeletonRow?: ReactNode;
  emptyState?: ReactNode;
  errorState?: (error: unknown, retry: () => void) => ReactNode;

  caption?: string;
  ariaLabel?: string;
  getRowAriaLabel?: (row: TRow) => string;

  onDataResolved?: (rows: TRow[]) => void;
  onSortChange?: (sorting: SortingState) => void;
  onFilterChange?: (filters: ColumnFiltersState) => void;
  onQueryStateChange?: (state: TableQueryState) => void;
  onViewChange?: (view: TableView) => void;
}

export function BmweDataTable<
  TRow extends Record<string, unknown>,
  TQuery,
  TVariables,
>(props: BmweDataTableProps<TRow, TQuery, TVariables>) {
  return (
    <TableViewProvider
      tableKey={props.tableKey}
      defaultViews={props.defaultViews}
      initialSort={props.initialSort}
      initialFilters={props.initialFilters}
      columns={props.columns}
    >
      <BmweDataTableInternal {...props} />
    </TableViewProvider>
  );
}

function BmweDataTableInternal<
  TRow extends Record<string, unknown>,
  TQuery,
  TVariables,
>({
  columns,
  infiniteQueryHook,
  toTableQueryVariables,
  mapToRows,
  getPageInfo,
  getTotalCount,
  tableKey,
  pageSize = 100,
  enableMultiSort = false,
  enableGlobalSearch = false,
  enableLayoutControls = false,
  globalSearchPlaceholder = tableStrings.messages.globalSearch,
  globalSearchSlot,
  searchOnSubmit = false,
  globalSearchInHeader = false,
  clearableGlobalSearch = false,
  loadingSkeletonRow,
  emptyState,
  errorState,
  caption,
  ariaLabel = 'Data table',
  getRowId: rowIdGetter,
  selectionMode = 'none',
  onSelectionChange,
  getRowAriaLabel,
  onDataResolved,
  onSortChange,
  onFilterChange,
  onQueryStateChange,
  onViewChange,
  debounceMs = 250,
  entityLabel,
  distinctValuesList,
}: BmweDataTableProps<TRow, TQuery, TVariables>) {
  const {
    currentView,
    sorting,
    setSorting,
    columnFilters,
    setFilters,
    updateView,
    createSnapshot,
    markSnapshotPreviewed,
  } = useTableView();

  const [globalSearch, setGlobalSearch] = useState('');
  const debouncedGlobalSearch = useDebounce(globalSearch, debounceMs);
  const appliedGlobalSearch = searchOnSubmit
    ? globalSearch
    : debouncedGlobalSearch;

  const {
    layoutMode,
    toggleLayoutMode,
    switchToScroll,
    columnWidths,
    startResize,
    autoFit,
    autoFitAll,
    fitColumnsToContent,
  } = useTableLayout(tableKey);

  const tableElementRef = useRef<HTMLTableElement | null>(null);
  const handleAutoFitAll = useCallback(() => {
    if (tableElementRef.current) {
      autoFitAll(tableElementRef.current);
    }
  }, [autoFitAll]);

  const [selectedRowIds, setSelectedRowIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const handleRowSelect = useCallback(
    (rowId: string) =>
      setSelectedRowIds((prev) => {
        if (selectionMode === 'multi') {
          const next = new Set(prev);
          if (!next.delete(rowId)) next.add(rowId);
          return next;
        }
        return prev.has(rowId) ? new Set() : new Set([rowId]);
      }),
    [selectionMode],
  );

  const debouncedSorting = useDebounce(sorting, debounceMs);
  const debouncedFilters = useDebounce(columnFilters, debounceMs);
  const filterMode = currentView.filterMode ?? 'and';

  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const headerSlot = useHeaderSlot();
  const searchInHeader = globalSearchInHeader && headerSlot !== null;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const withClearButton = (input: ReactNode) =>
    clearableGlobalSearch ? (
      <span className={styles.clearableSearch}>
        {input}
        <button
          type="button"
          className={styles.clearSearchButton}
          aria-label={tableStrings.messages.globalSearchClear}
          title={tableStrings.messages.globalSearchClear}
          onClick={() => {
            if (searchInputRef.current) searchInputRef.current.value = '';
            setGlobalSearch('');
            searchInputRef.current?.focus();
          }}
        >
          <i className="pi pi-times" aria-hidden />
        </button>
      </span>
    ) : (
      input
    );
  const placeSearchBar = (bar: ReactNode) =>
    searchInHeader ? createPortal(bar, headerSlot) : bar;
  const scrollParentRef = useRef<HTMLElement | null>(null);

  const assignTableContainerRef = useCallback((el: HTMLDivElement | null) => {
    if (!el) return;
    tableContainerRef.current = el;
    if (!scrollParentRef.current) {
      let ancestor: HTMLElement | null = el.parentElement;
      while (ancestor) {
        if (ancestor.hasAttribute('data-main-scroll')) {
          scrollParentRef.current = ancestor;
          break;
        }
        ancestor = ancestor.parentElement;
      }
    }
  }, []);

  const tableId = useMemo(
    () => `table-content-${Math.random().toString(36).slice(2, 10)}`,
    [],
  );

  const baseVariables = useMemo(
    () =>
      toTableQueryVariables({
        pageSize,
        cursor: undefined,
        sorting: debouncedSorting,
        filters: debouncedFilters,
        filterMode,
        globalSearch: appliedGlobalSearch || undefined,
      }),
    [
      toTableQueryVariables,
      pageSize,
      debouncedSorting,
      debouncedFilters,
      filterMode,
      appliedGlobalSearch,
    ],
  );

  const infiniteResult = infiniteQueryHook(baseVariables, {
    getNextPageParam: (lastPage) => {
      const pageInfo = getPageInfo(lastPage);
      if (!pageInfo || !pageInfo.hasNextPage) {
        return;
      }
      return { after: pageInfo.endCursor };
    },
    placeholderData: (prev) => prev,
    queryKey: [tableKey, baseVariables],
    initialPageParam: undefined,
  });

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetching: isFetchingAny,
    isFetchingNextPage,
    isPlaceholderData,
    isError,
    error,
    refetch,
    status,
  } = infiniteResult;

  const pages = useMemo(() => infiniteData?.pages ?? [], [infiniteData]);
  const resolvedRows: TRow[] = useMemo(
    () => mapToRows(pages),
    [pages, mapToRows],
  );

  // While results for a new search term load, the previous rows give way to
  // skeleton rows; filter and sort changes keep showing the previous rows.
  const resolvedSearchRef = useRef(appliedGlobalSearch);
  if (status === 'success' && !isPlaceholderData) {
    resolvedSearchRef.current = appliedGlobalSearch;
  }
  const searchPending =
    isPlaceholderData && resolvedSearchRef.current !== appliedGlobalSearch;
  const rowsData = searchPending ? (EMPTY_ROWS as TRow[]) : resolvedRows;

  // The server-provided totalCount stays consistent across pages of the same
  // query, so reading the latest page is enough. While a fetch is in flight
  // we keep the previous value (placeholderData) so the count doesn't blink.
  const totalCount = useMemo(
    () => (pages.length > 0 ? getTotalCount(pages[pages.length - 1]) : 0),
    [pages, getTotalCount],
  );

  const entityPlural = entityLabel?.plural ?? tableStrings.entity.plural;
  const entitySingular = entityLabel?.singular ?? tableStrings.entity.singular;
  const countLabel = tableStrings.messages.totalCount(
    totalCount,
    totalCount === 1 ? entitySingular : entityPlural,
  );

  const hasMore = hasNextPage && !searchPending;
  const virtualCount = searchPending
    ? SEARCH_SKELETON_ROWS
    : rowsData.length + (hasMore ? 1 : 0);

  const announcement = useSortAnnouncement(sorting, columns);

  useEffect(() => {
    onViewChange?.(currentView);
  }, [currentView, onViewChange]);
  useEffect(() => {
    onDataResolved?.(resolvedRows);
  }, [resolvedRows, onDataResolved]);
  useEffect(() => {
    onSortChange?.(sorting);
  }, [sorting, onSortChange]);
  useEffect(() => {
    onFilterChange?.(columnFilters);
  }, [columnFilters, onFilterChange]);
  useEffect(() => {
    onQueryStateChange?.({
      pageSize,
      cursor: undefined,
      sorting: debouncedSorting,
      filters: debouncedFilters,
      filterMode,
      globalSearch: appliedGlobalSearch || undefined,
    });
  }, [
    pageSize,
    debouncedSorting,
    debouncedFilters,
    filterMode,
    appliedGlobalSearch,
    onQueryStateChange,
  ]);

  const columnDefs = useMemo<ColumnDef<TRow>[]>(() => {
    const columnHelper = createColumnHelper<TRow>();
    const defaultCell: ColumnDefTemplate<CellContext<TRow, unknown>> = (info) =>
      info.getValue();

    const toDisplayColumn = (
      col: BmweDataColumn<TRow>,
      helper: ColumnHelper<TRow>,
    ) =>
      helper.display({
        id: col.id ?? col.field,
        header: col.header,
        cell: col.cell,
        meta: col.meta,
      });

    const toAccessorColumn = (
      col: BmweDataColumn<TRow>,
      helper: ColumnHelper<TRow>,
    ) => {
      const field = col.field;
      if (!field) {
        return toDisplayColumn(col, helper);
      }
      const id = col.id ?? field;
      const filterType = col.meta?.filterType ?? col.meta?.dataType;
      const meta =
        distinctValuesList && (!filterType || filterType === 'string')
          ? {
              ...col.meta,
              distinctValues: {
                queryKey: ['distinctValues', distinctValuesList, id],
                fetch: (search: string) =>
                  fetchDistinctValues(distinctValuesList, id, search),
              },
            }
          : col.meta;
      return helper.accessor((row) => getNestedProperty(row, field), {
        id,
        header: col.header,
        enableSorting: col.sortable,
        enableColumnFilter: col.filter ?? false,
        cell: col.cell ?? defaultCell,
        meta,
      });
    };

    return columns.map((col) =>
      col.field
        ? toAccessorColumn(col, columnHelper)
        : toDisplayColumn(col, columnHelper),
    );
  }, [columns, distinctValuesList]);

  const table = useReactTable<TRow>({
    columns: columnDefs,
    data: rowsData,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (originalRow, index, parent) =>
      (rowIdGetter ? rowIdGetter(originalRow, index) : undefined) ??
      `${parent ? parent.id + '.' : ''}${index}`,
    state: {
      sorting,
      columnFilters,
      columnVisibility: currentView?.visibleColumns,
      columnOrder: currentView?.columnOrder,
    },
    manualSorting: true,
    manualFiltering: true,
    enableMultiSort: enableMultiSort,
    sortDescFirst: false,
    debugTable: false,
    onSortingChange: (updater) => {
      setSorting((old) =>
        updater instanceof Function ? updater(old) : updater,
      );
      if (scrollParentRef.current) {
        scrollParentRef.current.scrollTop = 0;
      }
    },
    onColumnFiltersChange: (updater) => {
      setFilters((old) =>
        updater instanceof Function ? updater(old) : updater,
      );
      if (scrollParentRef.current) {
        scrollParentRef.current.scrollTop = 0;
      }
    },
  });

  const modelRows = table.getRowModel().rows;
  const selectedRows = useMemo(
    () =>
      modelRows
        .filter((row) => selectedRowIds.has(row.id))
        .map((row) => row.original),
    [modelRows, selectedRowIds],
  );
  useEffect(() => {
    if (selectionMode !== 'none') {
      onSelectionChange?.(selectedRows);
    }
  }, [selectionMode, selectedRows, onSelectionChange]);

  const rowVirtualizer = useVirtualizer({
    count: virtualCount,
    estimateSize: () => 33,
    getScrollElement: () =>
      scrollParentRef.current ?? tableContainerRef.current,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (el) => el?.getBoundingClientRect().height
        : undefined,
    overscan: 10,
  });

  const fetchMoreOnBottomReached = useCallback(
    (container?: HTMLDivElement | null) => {
      if (!container || isFetchingNextPage || !hasNextPage) {
        return;
      }
      const { scrollHeight, scrollTop, clientHeight } = container;
      if (scrollHeight - scrollTop - clientHeight < 300) {
        void fetchNextPage();
      }
    },
    [fetchNextPage, isFetchingNextPage, hasNextPage],
  );

  useEffect(() => {
    const el = scrollParentRef.current;
    if (!el) {
      return;
    }
    const handler = () => fetchMoreOnBottomReached(el as HTMLDivElement);
    el.addEventListener('scroll', handler);
    return () => el.removeEventListener('scroll', handler);
  }, [fetchMoreOnBottomReached]);

  const handleTableKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      (e.currentTarget as HTMLElement).focus();
    }
  }, []);

  const hiddenColumnOptions = columns
    .map((c) => ({ label: c.header, value: c.id ?? c.field ?? '' }))
    .filter((o) => o.value && !currentView?.visibleColumns[o.value]);

  // Shows the column at the end, like a preview: undo via the reset button,
  // kept by saving the view.
  const showColumn = (id: string) => {
    if (!currentView) return;
    // The shown columns keep their width; the table widens and scrolls instead.
    // The reset button undoes that switch together with the column.
    const undoLayout =
      enableLayoutControls && layoutMode === 'fit' && tableElementRef.current
        ? switchToScroll(tableElementRef.current)
        : undefined;
    createSnapshot(undoLayout);
    markSnapshotPreviewed();
    const order =
      currentView.columnOrder ??
      columns.map((c) => c.id ?? c.field ?? '').filter(Boolean);
    updateView({
      ...currentView,
      visibleColumns: { ...currentView.visibleColumns, [id]: true },
      columnOrder: [...order.filter((c) => c !== id), id],
    });
  };

  const columnMap = useMemo(
    () => new Map(columns.map((c) => [c.id ?? c.field, c])),
    [columns],
  );

  const visibleIds = useMemo(() => {
    const visibility = currentView?.visibleColumns ?? {};
    const order = currentView?.columnOrder;

    // Determine which columns are visible
    const visible = columns
      .map((c) => c.id ?? c.field)
      .filter((id): id is string => id != null && visibility[id]);

    // Apply column order if specified
    if (!order || order.length === 0) return visible;
    const orderSet = new Set(order);
    return [
      ...order.filter((id) => visible.includes(id)),
      ...visible.filter((id) => !orderSet.has(id)),
    ];
  }, [columns, currentView?.visibleColumns, currentView?.columnOrder]);

  // Scroll layout: stretchable columns without a stored width (e.g. just added)
  // get their content width before the first paint, not their `fr` × 200px.
  useLayoutEffect(() => {
    if (layoutMode !== 'scroll' || !tableElementRef.current) return;
    const unsized = visibleIds.filter((id) => {
      if (columnWidths[id]) return false;
      const track = parseTrack(columnMap.get(id)?.size);
      return track.fr !== undefined && !track.min;
    });
    if (unsized.length > 0) {
      fitColumnsToContent(tableElementRef.current, unsized);
    }
  }, [
    layoutMode,
    visibleIds,
    columnWidths,
    columnMap,
    fitColumnsToContent,
    rowsData.length,
  ]);

  const gridTemplateColumns = useMemo(() => {
    const tracks =
      layoutMode === 'scroll'
        ? visibleIds.map(
            (id) =>
              columnWidths[id] ??
              definedSizeToScrollPx(columnMap.get(id)?.size),
          )
        : toFitTracks(
            visibleIds.map((id) => ({
              size: columnMap.get(id)?.size,
              stored: columnWidths[id],
            })),
          );
    // The header's "+" (add column) cell; body rows leave it empty.
    return [...tracks, ACTION_TRACK].join(' ');
  }, [visibleIds, columnMap, columnWidths, layoutMode]);

  useEffect(() => {
    const el = (scrollParentRef.current ??
      tableContainerRef.current) as HTMLDivElement | null;
    fetchMoreOnBottomReached(el);
  }, [fetchMoreOnBottomReached, rowsData.length]);

  if (isError) {
    if (error instanceof Error && error.name === 'ForbiddenError') {
      throw error;
    }
    if (errorState) {
      return errorState(error, refetch);
    }
    return <ErrorFallback error={error} onRetry={() => refetch()} />;
  }

  const initialLoading = status === 'pending' && rowsData.length === 0;
  const noData = !initialLoading && rowsData.length === 0 && !isFetchingAny;

  // We're keeping the initialLoading check to show a loading indicator outside the table
  if (initialLoading) {
    return (
      <div role="status" aria-live="polite" className={styles.statusContainer}>
        <span>{tableStrings.messages.loading}</span>
      </div>
    );
  }

  return (
    <div
      ref={assignTableContainerRef}
      role="region"
      aria-label={ariaLabel}
      className={styles.dataTableContainer}
    >
      <div className={styles.metaRegion}>
        <a
          href={`#${tableId}`}
          className={`${styles.srOnly} ${styles.skipLink}`}
        >
          Skip to table content
        </a>
        <DataTableLiveRegions
          loading={isFetchingAny || isFetchingNextPage}
          announcement={announcement}
        />
      </div>

      <div>
        {placeSearchBar(
          <div
            className={
              searchInHeader
                ? `${styles.globalSearchWrapper} ${styles.globalSearchInHeader}`
                : `${styles.globalSearchWrapper} ${styles.pinnedStart}`
            }
          >
            {enableGlobalSearch &&
              (searchOnSubmit ? (
                <form
                  role="search"
                  className={styles.globalSearchForm}
                  onSubmit={(e) => {
                    e.preventDefault();
                    const input = e.currentTarget.elements.namedItem('q');
                    if (input instanceof HTMLInputElement) {
                      setGlobalSearch(input.value.trim());
                    }
                  }}
                >
                  {withClearButton(
                    <InputText
                      ref={searchInputRef}
                      name="q"
                      type="search"
                      defaultValue={globalSearch}
                      // An emptied field drops the search without waiting for submit.
                      onChange={(e) => {
                        if (!e.target.value) setGlobalSearch('');
                      }}
                      placeholder={globalSearchPlaceholder}
                      className={styles.globalSearchInput}
                      aria-label={globalSearchPlaceholder}
                    />,
                  )}
                  <Button
                    type="submit"
                    label={tableStrings.messages.globalSearchSubmit}
                    // Not `loading`: it disables the button, which blocks Enter.
                    icon={
                      isFetchingAny && !isFetchingNextPage
                        ? 'pi pi-spin pi-spinner'
                        : 'pi pi-search'
                    }
                    size="small"
                  />
                </form>
              ) : (
                withClearButton(
                  <InputText
                    ref={searchInputRef}
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    placeholder={globalSearchPlaceholder}
                    className={styles.globalSearchInput}
                    aria-label={globalSearchPlaceholder}
                  />,
                )
              ))}
            {globalSearchSlot}
            <span
              className={styles.totalCount}
              aria-live="polite"
              data-testid="bmwe-table-total-count"
            >
              {countLabel}
            </span>
          </div>,
        )}
        <div className={styles.pinnedStart}>
          <TableViewControl
            columns={columns}
            table={table}
            layoutMode={enableLayoutControls ? layoutMode : undefined}
            onToggleLayoutMode={
              enableLayoutControls ? toggleLayoutMode : undefined
            }
            onAutoFitAll={enableLayoutControls ? handleAutoFitAll : undefined}
          />
        </div>

        <table
          ref={tableElementRef}
          id={tableId}
          role="grid"
          className={styles.bmweDataTable}
          style={
            {
              '--gridTemplateColumns': gridTemplateColumns,
            } as CSSProperties
          }
          aria-rowcount={noData ? 2 : virtualCount + 1} // +1 for header row, or 2 for header + empty row
          aria-multiselectable={selectionMode === 'multi' || undefined}
          onKeyDown={handleTableKeyDown}
        >
          {caption && <caption className={styles.srOnly}>{caption}</caption>}
          <thead
            className={
              searchPending
                ? `${styles.bmweDataTableHead} ${styles.headPending}`
                : styles.bmweDataTableHead
            }
          >
            {table.getHeaderGroups().map((hg) => (
              <tr
                key={hg.id}
                role="row"
                aria-rowindex={1}
                className={styles.bmweDataTableRow}
              >
                {hg.headers.map((header, index) => (
                  <BmweTableHeader
                    key={header.id}
                    header={header}
                    onResizeStart={startResize}
                    onAutoFit={autoFit}
                    isLastColumn={index === hg.headers.length - 1}
                  />
                ))}
                <th className={styles.actionCell}>
                  {hiddenColumnOptions.length > 0 && (
                    <ColumnPickerMenu
                      options={hiddenColumnOptions}
                      onPick={showColumn}
                      label={tableStrings.tableView.addColumn}
                    />
                  )}
                </th>
              </tr>
            ))}
          </thead>
          <VirtualizedTableBody
            table={table}
            rowsData={rowsData}
            rowVirtualizer={rowVirtualizer}
            hasMore={hasMore}
            isFetchingNextPage={isFetchingNextPage}
            fetchNextPage={fetchNextPage}
            loadingSkeletonRow={loadingSkeletonRow}
            getRowAriaLabel={getRowAriaLabel}
            selectedRowIds={
              selectionMode !== 'none' ? selectedRowIds : undefined
            }
            onRowSelect={selectionMode !== 'none' ? handleRowSelect : undefined}
            columnsCount={columns.length}
            noData={noData}
            emptyState={emptyState}
          />
        </table>
      </div>
    </div>
  );
}
